import { ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useGetConversationTraces } from '@/hooks/useChat';
import { useWorkflowStatus } from '@/sections/insights/aiAssistant/hooks/useWorkflowStatus';
import { useTraceDrawerStore } from '@/sections/insights/aiAssistant/stores/traceDrawer.store';
import { Conversation, TraceEvent } from '@/types';
import { cn } from '@/utils/classname';

import { TraceHierarchy } from './TraceHierarchy';
import { WorkflowSpinner } from './WorkflowSpinner';

const MIN_WIDTH = 280;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 320;
const STORAGE_KEY = 'ai-assistant-workflow-drawer-width';

interface TraceDrawerProps {
  /** Custom width for the drawer (default: '320px') - only used if no saved width */
  width?: string;
  /** Trace ID of the currently-selected conversation (undefined if no workflow) */
  traceId?: string;
  childConversations?: Conversation[];
  currentConversationId?: string;
  parentConversationId?: string;
  onNavigateToConversation?: (conversationId: string) => void;
}

// Calculate workflow stats
function calculateWorkflowStats(traces: TraceEvent[]) {
  if (!traces || traces.length === 0) {
    return { totalDurationMs: 0, stepCount: 0 };
  }

  let earliest = Infinity;
  let latest = -Infinity;
  const spanIds = new Set<string>();

  traces.forEach(event => {
    if (!event) return;
    const cleanedTime = event.created?.includes('#')
      ? event.created.split('#').filter(Boolean).pop() || event.created
      : event.created;
    const normalizedTime = cleanedTime?.replace(/\.\d{4,}Z$/, match => {
      const ms = match.slice(1, 4);
      return `.${ms}Z`;
    });
    const time = new Date(normalizedTime || '').getTime();
    if (!isNaN(time)) {
      if (time < earliest) earliest = time;
      if (time > latest) latest = time;
    }
    if (event.span_id) {
      spanIds.add(event.span_id);
    }
  });

  if (earliest === Infinity || latest === -Infinity) {
    return { totalDurationMs: 0, stepCount: spanIds.size };
  }

  return {
    totalDurationMs: Math.max(0, latest - earliest),
    stepCount: spanIds.size,
  };
}

function formatTotalDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSec = seconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${remainingSec}s`;
  }
  return `${seconds}s`;
}

/**
 * TraceDrawer - Side drawer displaying workflow trace hierarchy.
 * Slides in from the right side of the conversation area.
 * Resizable by dragging the left edge.
 */
export const TraceDrawer: React.FC<TraceDrawerProps> = ({
  width: defaultWidth = '320px',
  traceId,
  childConversations = [],
  currentConversationId,
  parentConversationId,
  onNavigateToConversation,
}) => {
  const { isOpen, activeTab, close, setActiveTab } = useTraceDrawerStore();
  const { data: traces = [] } = useGetConversationTraces(traceId);
  const typedTraces = traces as TraceEvent[];
  const { isRunning, isComplete } = useWorkflowStatus(typedTraces);
  const hasSubagents = childConversations.length > 0;
  const hasWorkflow = !!traceId;

  // Reconcile drawer state whenever the selected conversation changes.
  // If neither tab has content, close the drawer; if the active tab
  // has no content but the other does, switch to it. Prevents stale
  // trace/subagent data from showing after switching conversations.
  useEffect(() => {
    if (!isOpen) return;
    if (!hasWorkflow && !hasSubagents) {
      close();
    } else if (activeTab === 'workflow' && !hasWorkflow) {
      setActiveTab('subagents');
    } else if (activeTab === 'subagents' && !hasSubagents) {
      setActiveTab('workflow');
    }
  }, [isOpen, activeTab, hasWorkflow, hasSubagents, close, setActiveTab]);

  // Initialize width from localStorage or default
  const [drawerWidth, setDrawerWidth] = useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= MIN_WIDTH && parsed <= MAX_WIDTH) {
        return parsed;
      }
    }
    const defaultParsed = parseInt(defaultWidth, 10);
    return !isNaN(defaultParsed) ? defaultParsed : DEFAULT_WIDTH;
  });

  const [isResizing, setIsResizing] = useState(false);

  const workflowStats = useMemo(
    () => calculateWorkflowStats(typedTraces),
    [typedTraces]
  );

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      const clampedWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, newWidth));
      setDrawerWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      localStorage.setItem(STORAGE_KEY, drawerWidth.toString());
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Prevent text selection while resizing
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing, drawerWidth]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ x: '100%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: '100%', opacity: 0 }}
          transition={{
            type: 'tween',
            duration: 0.25,
            ease: 'easeOut',
          }}
          className="bg-chariot-background-primary border-chariot-stroke absolute top-0 right-0 z-40 flex h-full flex-col border-l shadow-lg"
          style={{ width: `${drawerWidth}px` }}
        >
          {/* Resize handle */}
          <div
            onMouseDown={handleMouseDown}
            className="absolute top-0 left-0 h-full w-1 cursor-ew-resize transition-colors hover:bg-blue-500/50"
            title="Drag to resize"
          />
          {/* Header with tabs */}
          <div className="border-chariot-stroke border-b">
            <div className="flex items-center justify-between px-4 pt-3 pb-0">
              <div className="flex gap-4">
                {hasWorkflow && (
                  <button
                    onClick={() => setActiveTab('workflow')}
                    className={cn(
                      'relative pb-2 text-sm font-medium transition-colors',
                      activeTab === 'workflow'
                        ? 'text-chariot-text-primary'
                        : 'text-chariot-text-tertiary hover:text-chariot-text-secondary'
                    )}
                  >
                    <span className="flex items-center gap-1.5">
                      Workflow
                      {isRunning && (
                        <span className="bg-chariot-stroke-info size-1.5 animate-pulse rounded-full" />
                      )}
                      {isComplete && (
                        <span className="text-[10px] text-green-500">✓</span>
                      )}
                    </span>
                    {activeTab === 'workflow' && (
                      <span className="bg-chariot-brand-primary absolute bottom-0 left-0 h-0.5 w-full rounded-full" />
                    )}
                  </button>
                )}
                {hasSubagents && (
                  <button
                    onClick={() => setActiveTab('subagents')}
                    className={cn(
                      'relative pb-2 text-sm font-medium transition-colors',
                      activeTab === 'subagents'
                        ? 'text-chariot-text-primary'
                        : 'text-chariot-text-tertiary hover:text-chariot-text-secondary'
                    )}
                  >
                    <span className="flex items-center gap-1.5">
                      Subagents
                      <span className="bg-chariot-background-secondary text-chariot-text-tertiary rounded-full px-1.5 text-[10px]">
                        {childConversations.length}
                      </span>
                    </span>
                    {activeTab === 'subagents' && (
                      <span className="bg-chariot-brand-primary absolute bottom-0 left-0 h-0.5 w-full rounded-full" />
                    )}
                  </button>
                )}
              </div>
              <button
                onClick={close}
                className="text-chariot-text-secondary hover:text-chariot-text-primary rounded p-1 transition-colors"
                aria-label="Close drawer"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>
            {activeTab === 'workflow' && workflowStats.stepCount > 0 && (
              <div className="text-chariot-text-tertiary px-4 pb-2 text-[10px]">
                {workflowStats.stepCount} step
                {workflowStats.stepCount !== 1 ? 's' : ''} &middot;{' '}
                {formatTotalDuration(workflowStats.totalDurationMs)} total
              </div>
            )}
          </div>

          {/* Workflow tab content */}
          {activeTab === 'workflow' && (
            <>
              <div className="flex-1 overflow-y-auto p-4">
                <TraceHierarchy traces={typedTraces} animateEntries />
              </div>
              {isRunning && (
                <div className="border-chariot-stroke border-t">
                  <WorkflowSpinner size="sm" />
                </div>
              )}
            </>
          )}

          {/* Subagents tab content */}
          {activeTab === 'subagents' && (
            <div className="flex-1 overflow-y-auto">
              {parentConversationId && parentConversationId !== 'self' && (
                <button
                  onClick={() =>
                    onNavigateToConversation?.(parentConversationId)
                  }
                  className="text-chariot-brand-primary hover:bg-chariot-background-secondary border-chariot-stroke flex w-full items-center gap-1 border-b px-4 py-2 text-xs"
                >
                  <ChevronLeftIcon className="size-3" />
                  Back to parent
                </button>
              )}
              <div className="space-y-1 p-3">
                {childConversations.map(child => {
                  const childId = child.uuid || child.id || '';
                  const isViewing = childId === currentConversationId;
                  return (
                    <button
                      key={childId}
                      onClick={() => onNavigateToConversation?.(childId)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                        isViewing
                          ? 'bg-chariot-brand-primary/10 text-chariot-brand-primary'
                          : 'text-chariot-text-secondary hover:bg-chariot-background-secondary hover:text-chariot-text-primary'
                      )}
                    >
                      {child.status === 'active' ? (
                        <span className="relative flex size-2 flex-shrink-0">
                          <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                          <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
                        </span>
                      ) : (
                        <span className="bg-chariot-text-tertiary/40 size-2 flex-shrink-0 rounded-full" />
                      )}
                      <span className="truncate">
                        {child.topic || `Agent@${childId.slice(-8)}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TraceDrawer;
